import memoize from 'lodash/memoize'

import {
  getRefundType,
  RefundType
} from '../../../offer/business/cancellationPenalties'
import {isSplitBookingPriceEligible} from '../../../offer/business/isSplitBookingPriceEligible'
import {hasBreakfast} from '../../../offer/business/meal'
import {
  hasChargeLaterPayment,
  hasPayAtPropertyPayment
} from '../../../offer/business/offerPaymentMode'
import {Offer} from '../../../offer/types/offer'
import {getDisplayPrice, getTotalPrice} from '../../../price/business/price'
import {TransformedRoomsResponse} from '../../../sapi/services/searchApi'
import {Room, SplitBooking} from '../../types/room'
import {sortRoomsByNightlyDisplayTotalAsc} from './sortRooms'

/**
 * All supported filters
 */
export enum RoomsFilterKey {
  FreeCancellation = 'freeCancellation',
  BreakfastIncluded = 'breakfastIncluded',
  PayLater = 'payLater',
  DealFreeze = 'dealFreeze'
}

export enum FilterStrategy {
  Some = 'some',
  Every = 'every'
}

export type RoomsFilterParameters = Partial<
  Record<(typeof RoomsFilterKey)[keyof typeof RoomsFilterKey], boolean>
>

export interface FilteredRoomsData extends TransformedRoomsResponse {
  rooms: Room[]
  totalResults: number
  isSplitBookingAvailable: boolean
  availableFilters: RoomsFilterParameters
}

const filterPredicates = {
  // Deliberate OR: the rooms "Pay later" filter matches BOTH products
  // (pay-at-property and charge later)
  payLater: (offer: Offer) => {
    return hasPayAtPropertyPayment(offer) || hasChargeLaterPayment(offer)
  },
  breakfastIncluded: (offer: Offer) => hasBreakfast(offer.mealPlan),
  freeCancellation: (offer: Offer) =>
    getRefundType(offer.cancellationPenalties) === RefundType.freeCancellation,
  dealFreeze: (offer: Offer) => offer.tags?.includes('deal_freeze')
}

/**
 * Predicates for each room filter, using "some" method by default
 */
const filterConditions = {
  payLater: (offers: Offer[]) => offers.some(filterPredicates.payLater),
  breakfastIncluded: (offers: Offer[]) =>
    offers.some(filterPredicates.breakfastIncluded),
  freeCancellation: (offers: Offer[]) =>
    offers.some(filterPredicates.freeCancellation),
  dealFreeze: (offers: Offer[]) => offers.some(filterPredicates.dealFreeze)
}

/**
 * Predicates for each room filter, using "every" method
 */
const filterConditionsEvery = {
  payLater: (offers: Offer[]) => offers.every(filterPredicates.payLater),
  breakfastIncluded: (offers: Offer[]) =>
    offers.every(filterPredicates.breakfastIncluded),
  freeCancellation: (offers: Offer[]) =>
    offers.every(filterPredicates.freeCancellation),
  dealFreeze: (offers: Offer[]) => offers.every(filterPredicates.dealFreeze)
}

/**
 * Function that generates an offer list based on the active filters.
 * @param offers - Array of offers to filter
 * @param activeFilters - Array of filter keys to apply
 * @returns An array of offers that meet the active filters.
 */
export const filterOffersWithActiveFilters = (
  offers: Offer[],
  activeFilters: RoomsFilterKey[]
) => {
  return offers.filter(offer =>
    activeFilters.every(filterKey =>
      filterConditions[filterKey] ? filterConditions[filterKey]([offer]) : false
    )
  )
}

/**
 * Applies active filters to a list of room objects.
 * Will remove the room object completely if all offers are filtered out
 * Also updates hasClickedOffer if clicked offer was filtered out
 * @param rooms - list of room objects that have a list of offers inside
 * @param filters - list of active room filters
 * @returns An array of rooms that meet the active filters.
 */
const filterRoomsAndOffers = (rooms: Room[], filters: RoomsFilterKey[]) =>
  rooms.reduce<Room[]>((acc, room) => {
    const filteredOffers = filterOffersWithActiveFilters(room.offers, filters)
    const hasClickedOffer = filteredOffers.some(offer => offer.isClicked)
    if (filteredOffers.length > 0) {
      return [...acc, {...room, hasClickedOffer, offers: filteredOffers}]
    }

    return acc
  }, [])

/**
 * Filters split booking offers based on the active filters
 * @param splitBooking - The split booking to filter
 * @param filters - The active filters to apply
 * @returns The filtered split booking offers or undefined
 */
export const filterSplitBooking = (
  splitBooking: SplitBooking | undefined,
  filters: RoomsFilterKey[]
) => {
  const flatSplitBookingOffers =
    splitBooking?.offers.map(offer => offer.offer) || []

  return filterOffersWithActiveFilters(flatSplitBookingOffers, filters)
    .length === flatSplitBookingOffers.length
    ? splitBooking
    : undefined
}

/**
 * Returns all the filters available for the split booking data set
 * @param splitBooking - Optional split booking data to check for available filters
 * @returns An object that each key is a filter ID and the value is a boolean indicating if the filter is available.
 */
export const getAvailableFiltersFromSplitBooking = (
  splitBooking: SplitBooking | undefined
) => {
  const offers = splitBooking?.offers.map(offer => offer.offer) || []

  return getAvailableFilters(offers, FilterStrategy.Every)
}

/**
 * Returns all the filters available for the rooms response data set (splitBooking and regular rooms)
 * @param rooms - Array of rooms to check for available filters
 * @param splitBooking - Optional split booking data to check for available filters
 * @returns An object with filter flags set to true when available
 */
export const getFiltersAvailableForRoomsResult = (
  rooms: Room[],
  splitBooking?: SplitBooking
) => {
  const flatRoomOffers = rooms
    .map(room => room.offers)
    .reduce((acc, cur) => [...acc, ...cur], [])
  const availableRoomFilters = getAvailableFilters(flatRoomOffers)
  const availableSplitBookingFilters =
    getAvailableFiltersFromSplitBooking(splitBooking)

  return Object.fromEntries(
    Object.values(RoomsFilterKey).map(filterId => [
      filterId,
      Boolean(
        availableRoomFilters[filterId] || availableSplitBookingFilters[filterId]
      )
    ])
  )
}

/**
 * Apply rooms filters and build metadata required for filters
 * @param data - Object containing rooms and split booking data
 * @param data.rooms - Array of rooms to filter
 * @param data.splitBooking - Optional split booking data
 * @param filters - Array of filter keys to apply
 * @returns An object containing the filtered list of rooms, the total number of results, and the available filters
 */
export const applyRoomsFilter = (
  {
    rooms,
    splitBooking: responseSplitBooking,
    ...rest
  }: TransformedRoomsResponse,
  filters: RoomsFilterKey[]
): FilteredRoomsData => {
  const filteredRooms = filters?.length
    ? sortRoomsByNightlyDisplayTotalAsc(filterRoomsAndOffers(rooms, filters)) // Re-sort because we may have removed the cheaper offers
    : rooms
  const cheapestOffer = filteredRooms[0]?.offers[0]
  const cheapestOfferTotalPrice = cheapestOffer
    ? getTotalPrice(getDisplayPrice(cheapestOffer.prices))
    : 0
  const isSplitBookingOfferTheCheapest = isSplitBookingPriceEligible(
    responseSplitBooking?.totalRate,
    cheapestOfferTotalPrice
  )
  const splitBooking =
    isSplitBookingOfferTheCheapest || responseSplitBooking?.isClicked
      ? responseSplitBooking
      : undefined

  const availableFilters = getFiltersAvailableForRoomsResult(
    rooms,
    splitBooking
  )

  return {
    ...rest,
    rooms: filteredRooms,
    splitBooking: filterSplitBooking(splitBooking, filters),
    isSplitBookingAvailable: !!splitBooking,
    totalResults: rooms.length + (splitBooking ? 1 : 0),
    availableFilters
  }
}

/**
 * Generate a cache key from an array of filter keys
 * @param filters - Array of filter keys to generate cache key from
 * @returns Cache key string
 */
export const filtersToCacheKey = (filters: RoomsFilterKey[]) =>
  filters.slice(0).sort().join()

/**
 * Generate a cache key that includes the essential room offers data
 * @param data - The data to generate the cache key from
 * @returns A cache key
 */
export const roomsToCacheKey = (data: TransformedRoomsResponse) => {
  const {rooms, splitBooking, status} = data || {}
  const key = [data?.searchId, status?.complete]

  if (rooms?.length) {
    key.push(
      rooms
        .map(room => {
          return `${room?.id}:${room?.offers?.length}`
        })
        .join(',')
    )
  }

  if (splitBooking) key.push(`split:${splitBooking?.offers?.length}`)

  return key.join('.')
}

/**
 * Apply rooms filter with memoization by searchId + filters as a cache key
 * Prefer using this because memoizing selectors and component state is very fragile. Note that the key generator may still run many times.
 */
export const applyRoomsFilterMemo = memoize(
  applyRoomsFilter,
  (data, filters) => {
    // When using long polling, we need to include more room information since multiple room requests can share the same searchId
    return `${roomsToCacheKey(data)}.${filtersToCacheKey(filters)}`
  }
)

/**
 * Function that returns an object indicating which filters are available, based on room offers data
 * @param offers - Array of room offers to check for available filters
 * @param strategy - Strategy to use when checking filter availability (some/every)
 * @returns An object that each key is a filter ID and the value is a boolean indicating if the filter is available.
 */
export const getAvailableFilters = (
  offers: Offer[],
  strategy: FilterStrategy = FilterStrategy.Some
) => {
  const conditions =
    strategy === FilterStrategy.Some ? filterConditions : filterConditionsEvery

  return Object.fromEntries(
    Object.values(RoomsFilterKey).map(filterId => [
      filterId,
      offers.length ? conditions[filterId](offers) : false
    ])
  ) as RoomsFilterParameters
}

/**
 * Convert a list of filter keys to a parameters object
 * @param activeFilters - Array of filter keys to convert to a parameters object
 * @returns An object where each key is a filter ID, and the value is a boolean indicating if the filter is in the list
 */
export const roomsFilterListToMap = (activeFilters: RoomsFilterKey[]) => {
  return Object.fromEntries(
    Object.values(RoomsFilterKey).map(filterId => [
      filterId,
      activeFilters.includes(filterId)
    ])
  ) as RoomsFilterParameters
}

/**
 * Convert an object of SRP filters to AP Rooms filters
 * @param searchFilters - Object containing search filters
 * @param searchFilters.freeCancellation - Optional free cancellation flag
 * @param searchFilters.amenities - Optional array of amenity strings
 * @param searchFilters.dealFreeze - Optional deal freeze flag
 * @returns a list of RoomsFilterKey values compatible with Rooms filters
 */
export const mapSearchToRoomsFilters = (searchFilters: {
  // eslint-disable-next-line no-undef
  freeCancellation?: BooleanUrlParam
  amenities?: string | string[]
  dealFreeze?: BooleanUrlParam // Added as a part of c747927f-deal-freeze-srp
}) => {
  const hasBreakfastIncluded = searchFilters.amenities?.includes('breakfast')
  const hasFreeCancellation = Boolean(searchFilters.freeCancellation)
  const hasDealFreeze = Boolean(searchFilters.dealFreeze)
  const roomsFilters: Array<RoomsFilterKey> = []
  if (hasBreakfastIncluded) {
    roomsFilters.push(RoomsFilterKey.BreakfastIncluded)
  }
  if (hasFreeCancellation) {
    roomsFilters.push(RoomsFilterKey.FreeCancellation)
  }
  if (hasDealFreeze) {
    roomsFilters.push(RoomsFilterKey.DealFreeze)
  }

  return roomsFilters
}
